import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box, Button, Drawer, Grid, InputAdornment, Stack, Typography } from "@mui/material";
import { SubscriptionOptions } from "@reduxjs/toolkit/query";

import { DeleteConfirmModal, DynamicTable, IconImage, PlaceholderCard, TradeDataExportModal } from "@/components";
import { black_filter_img, search_img, sort_img, upload_img, delete_img, crash_warning } from "@/constants";
import { useAppDispatch, useAppSelector } from "@/store";
import {
  startPolling,
  stopPolling,
  tradeDataApi,
  useDeleteTradeDataMutation,
  useGetAllTradeDataQuery,
  useLazyGetAllTradeDataLogsQuery,
  usePostTradeDataMutation,
} from "@/services";
import { useDebounce, useSearchQuery, useSnackbar } from "@/hooks";
import { roles } from "@/roles";
import { columns, defaultVisibleColumns } from "@/columns/COMMON/trade-data";
import ProcessBox from "./ProcessBox";
import { getPollingInterval } from "@/utils";
import {
  Backdrop,
  LeftFilters,
  RightFilters,
  StyledContainedButton,
  StyledDeleteContainedButton,
  StyledHeadeButtons,
  StyledMenuPopover,
} from "./style";
import TimelineData from "./Timeline";
import UploadData from "./upload-data";
import { IOSSwitch } from "@/components/switch/styles";
import { HeaderSearchBox, StyledTextInput } from "../documents/styles";

const INITIAL_PAGE = 1;

interface ColumnType extends TradeDataTypes {
  isHidden: boolean;
}

const TradeData: FC = (): ReactElement => {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [page, setPage] = useState(INITIAL_PAGE);
  const [limit, setLimit] = useState(10);
  const [sortOrder, setSortOrder] = useState<"asc" | "desc" | "">("");
  const [sortType, setSortType] = useState<
    "entryDateSeconds" | "portOfEntryCode"  
  >("entryDateSeconds");
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [columnsMenuAnchor, setColumnsMenuAnchor] =
    useState<HTMLButtonElement | null>(null);
  const [filteredColumns, setFilteredColumns] = useState<IColumn<ColumnType>[]>(
    [
      ...columns.map(
        (column) =>
          ({
            ...column,
            isHidden: !defaultVisibleColumns.includes(column.key),
          } as IColumn<ColumnType>)
      ),
    ]
  );
  const [showLogs, setShowLogs] = useState(false);
  const [showUploadDataModal, setShowUploadDataModal] =
    useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const progressSubscriberRef = useRef<
    ((options: SubscriptionOptions) => void) | null
  >(null);

  const { updateQuery, getQueryValue } = useSearchQuery();
  const dispatch = useAppDispatch();
  const { isPollingActive } = useAppSelector((state) => state.polling); // Get polling status from Redux store
  const { selectedClient } = useAppSelector((state) => state.client);
  const { selectedTenant } = useAppSelector((state) => state.tenantManagement);
  const { user } = useAppSelector((state) => state.auth);
  const { show } = useSnackbar();
  const [timeStamp, setTimeStamp] = useState<string>(new Date().toISOString());
  const canSelectClients = useMemo(() => {
    const allowedRoles = [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN] as string[];
    return allowedRoles.includes(user?.userRole ?? "");
  }, [user]);

  const shouldSkip = useMemo(
    () => {
      if (user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "TRADEDIVE_CLIENT") {
        return false; // Allow query to run for TDADMIN with TradeDive Client, even if selectedClient is not set
      }
      return canSelectClients && !selectedClient;
    },
    [canSelectClients, selectedClient, selectedTenant, user]
  );

  const isAdmin = useMemo(() => {
    return user?.userRole === roles.TDADMIN && selectedTenant?.tenantType === "SERVICE_PROVIDER";
  }, [user, selectedTenant]);

  const [fetchUploadProgress, { data: uploadProgress, error: progressError }] =
    useLazyGetAllTradeDataLogsQuery();


  const {
    data: apiData,
    isLoading,
    isFetching,
  } = useGetAllTradeDataQuery(
    {
      pageSize: limit,
      pageNo: page,
      searchKeyword: getQueryValue("search"),
      ...(sortOrder && { sortType: sortOrder === "asc" ? 1 : -1 }),
      ...(sortType && { sortBy: sortType }),
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    },
    {
      refetchOnMountOrArgChange: true,
      skip: shouldSkip,
    }
  );

  const [postTradeData, { isLoading: isCreatingTradeData }] =
    usePostTradeDataMutation();

  const [deleteTradeData, { isLoading: isDeletingTradeData }] =
    useDeleteTradeDataMutation();
  const canUploadData = useMemo(() => {
    return (
      !!user &&
      [roles.SPOWNER, roles.SPPARTNER, roles.TDCLIENT, roles.TDADMIN].includes(user.userRole)
    );
  }, [user]);

  const filteredData = useMemo(() => {
    return (apiData?.getAllTradeData?.data || []).filter(
      ({ entryDate }) => entryDate
    );
  }, [apiData]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const showColumnsMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setColumnsMenuAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setPage(INITIAL_PAGE);
    setAnchorEl(null);
  };

  const handleSort = (order: "asc" | "desc") => {
    setSortOrder(order);
    handleClose();
  };

  const handleCol = (key: string) => {
    setFilteredColumns((prevFilteredColumns) => {
      return prevFilteredColumns.map((prevFilteredCol) => {
        if (key === prevFilteredCol.key) {
          return {
            ...prevFilteredCol,
            isHidden: !(prevFilteredCol as any).isHidden,
          };
        }

        return prevFilteredCol;
      });
    });
  };

  const handleDelete = async () => {
    const params: DeleteTradeDataVariables = {
      tenantId: "",
      clientId: ""
    };

    if (user?.userRole === roles.TDCLIENT) {
      params.tenantId = user.tenantId;
    } else if (user?.userRole && [roles.SPOWNER, roles.SPPARTNER].includes(user.userRole)) {
      params.tenantId = user?.tenantId ?? "";
      params.clientId = selectedClient?._id ?? "";
    } else if (user?.userRole === roles.TDADMIN) {
      params.tenantId = selectedTenant?._id ?? "";
      params.clientId = selectedClient?._id ?? "";
    }

   const res = await deleteTradeData(params);
   if(res.error) {
    console.log("res", res.error)
    show("Error deleting trade data", "", "error");
   } else {
    show("Trade data deleted successfully", "", "success");
    setDeleteModalOpen(false)
    dispatch(tradeDataApi.util.invalidateTags(["tradeData"]));
   }
  };

  const open = Boolean(anchorEl);
  const id = open ? "simple-popover" : undefined;

  const pullProgress = useCallback(async () => {
    // Determine polling interval based on current task status
    const pollingInterval = uploadProgress ? getPollingInterval([uploadProgress]) : 15000;

    const progressSubscriber = fetchUploadProgress({
      input: {
        clientId: selectedClient?._id ?? user?._id ?? "",
        uploadTime:timeStamp,
        ...(user?.userRole === roles.TDADMIN && selectedTenant && {
          tenantId: selectedTenant._id
        }),
      },
    });

    // Update subscription with adaptive polling interval
    progressSubscriber.updateSubscriptionOptions({ pollingInterval });

    dispatch(startPolling());
    progressSubscriberRef.current =
      progressSubscriber.updateSubscriptionOptions;
  }, [fetchUploadProgress, selectedClient, dispatch, timeStamp, uploadProgress]);

  // page change polling
  useEffect(() => {
    if (isPollingActive) {
      pullProgress();
    }
  }, [isPollingActive, pullProgress]);

  // useEffect(() => {
  //   // skip the fetching if the client is not selected
  //   if (shouldSkip) return;

  //   pullProgress();
  // }, [pullProgress, shouldSkip]);

  useEffect(() => {
    if (
      progressSubscriberRef.current &&
      (progressError || uploadProgress?.progress === 100)
    ) {
      progressSubscriberRef.current({ pollingInterval: 0 });
      progressSubscriberRef.current = null;
      dispatch(stopPolling());
    }

    if (uploadProgress?.progress === 100) {
      dispatch(tradeDataApi.util.invalidateTags(["tradeData"]));
      setPage(INITIAL_PAGE);
    }
  }, [uploadProgress, progressError, dispatch]);

  // Update polling interval based on task health
  useEffect(() => {
    if (progressSubscriberRef.current && uploadProgress) {
      const newPollingInterval = getPollingInterval([uploadProgress]);
      progressSubscriberRef.current({ pollingInterval: newPollingInterval });
    }
  }, [uploadProgress]);

  const createTradeDataFn = async (param: DropzoneTypes[]) => {
    if (param.length === 0) {
      return show("Please upload a file", "", "error");
    }

    const uploadedFile = param[0];
    if (!uploadedFile.fileUrl) {
      return show("File upload incomplete", "", "error");
    }

    const payload: CreateTradeDataDTO["tradeData"] = {
      tradeDataFile: uploadedFile.fileUrl,
      ...(user?.userRole &&
        [roles.SPOWNER, roles.SPPARTNER, roles.TDADMIN].includes(user.userRole) && {
          clientId: selectedClient?._id,
        }),
      ...(user?.userRole &&
        [roles.TDADMIN].includes(user.userRole) && {
          tenantId: selectedTenant?._id,
        }),
    };

    try {
      await postTradeData({
        tradeData: {
          ...payload,
        },
      }).unwrap();
      show("Data successfully uploaded", "", "success");
      setShowUploadDataModal(false);

      await pullProgress();
    } catch (error: any) {
      console.error("Upload error:", error);
      show(
        error?.data?.errors?.[0]?.message || 
        "Failed to upload data. Please try again.", 
        "", 
        "error"
      );
    }
  };

  const filterTradeData = useDebounce<string>((search) => {
    updateQuery({ search });
    setPage(1);
  }, 1200);

  return (
    <>
    {(shouldSkip && isAdmin)? (
      <Stack sx={{ flex: 1, justifyContent: "center", alignItems: "center" }}>
        <Typography sx={{ fontSize: 24, fontWeight: 500, color: "#000" }}>Please select a client to view the trade data</Typography>
      </Stack>
    ) : (
      <Stack sx={{ flex: 1 }}>
        <StyledHeadeButtons container spacing={1.5}>
        <Grid item xs={12} md={12} lg={6}>
          <LeftFilters>
            <Box style={{ display: "none" }}>
              <StyledContainedButton
                size="large"
                title="Filter"
                variant="outlined"
                startIcon={<IconImage src={black_filter_img} size="auto" />}
              />
            </Box>

            <Box>
              <StyledContainedButton
                size="large"
                title="Sort"
                variant="outlined"
                onClick={handleClick}
                aria-describedby={id}
                startIcon={<IconImage src={sort_img} size="auto" />}
              />
            </Box>
            <StyledContainedButton
              size="large"
              title="Show Columns"
              variant="outlined"
              onClick={showColumnsMenu}
              startIcon={<IconImage src={black_filter_img} size="auto" />}
            />
            {filteredData.length > 0 && (
              <StyledDeleteContainedButton
                size="large"
                title={"Delete"}
                variant="outlined"
                onClick={() => setDeleteModalOpen(true)}
                startIcon={<IconImage src={delete_img} size="21px" />}
              />
            )}
            <DeleteConfirmModal
              btnLoader={isDeletingTradeData} 
              open={deleteModalOpen}
              onClose={() => setDeleteModalOpen(false)}
              maxWidth="xs"
              onConfirm={handleDelete}
              subText="history of trade data"
            />
          </LeftFilters>
          {open || (!!columnsMenuAnchor && <Backdrop onClick={handleClose} />)}
          <StyledMenuPopover
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{ width: "231px" }}
            onClose={handleClose}
            open={open}
            anchorEl={anchorEl}
          >
            <Box>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  backgroundColor: sortOrder === "asc" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                onClick={() => handleSort("asc")}
                style={{ cursor: "pointer" }}
              >
                Ascending
              </Typography>
              <Typography
                variant="body1"
                fontWeight={500}
                sx={{
                  backgroundColor: sortOrder === "desc" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                onClick={() => handleSort("desc")}
                style={{ cursor: "pointer" }}
              >
                Descending
              </Typography>
              <Typography
                onClick={() => {
                  setSortType("portOfEntryCode");
                  handleClose();
                }}
                sx={{
                  backgroundColor:
                    sortType === "portOfEntryCode" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                variant="body1"
                fontWeight={500}
              >
                Port of Entry
              </Typography>
              <Typography
                variant="body1"
                onClick={() => {
                  setSortType("entryDateSeconds");
                  handleClose();
                }}
                sx={{
                  backgroundColor:
                    sortType === "entryDateSeconds" ? "#0B494B26" : "unset",
                  borderRadius: "4px",
                }}
                fontWeight={500}
              >
                Entry Date
              </Typography>
            </Box>
          </StyledMenuPopover>
          <StyledMenuPopover
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "left",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "left",
            }}
            sx={{
              width: "400px",
              "& .MuiPaper-root": { maxHeight: "450px", overflow: "auto" },
            }}
            onClose={() => setColumnsMenuAnchor(null)}
            open={!!columnsMenuAnchor}
            anchorEl={columnsMenuAnchor}
          >
            <Stack>
              {filteredColumns.map((col) => {
                return (
                  <Stack
                    key={col.key}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                  >
                    <Typography
                      variant="body1"
                      fontWeight={500}
                      sx={{
                        cursor: "default !important",
                        background: "inherit !important",
                      }}
                    >
                      {col.name}
                    </Typography>
                    <IOSSwitch
                      disabled={(() => {
                        const enabledColumns = filteredColumns.filter(
                          (filCol: any) => !filCol.isHidden
                        );

                        const isLastColEnabled = enabledColumns.length === 1;

                        if (
                          isLastColEnabled &&
                          enabledColumns[0].key === col.key
                        ) {
                          return true;
                        }

                        return false;
                      })()}
                      checked={
                        filteredColumns.filter(
                          (filCol: any) =>
                            filCol.name === col.name && !filCol.isHidden
                        ).length > 0
                      }
                      onChange={() => handleCol(col.key)}
                    />
                  </Stack>
                );
              })}
            </Stack>
          </StyledMenuPopover>
        </Grid>
        <Grid item xs={12} md={12} lg={6}>
          <Stack
            direction="row"
            justifyContent="flex-end"
            alignItems="center"
            gap={2}
          >
           
            <RightFilters>
              <Box>
                <StyledContainedButton
                  size="large"
                  sx={{width:"208px"}}
                  title={"Export CSV"}
                  variant="outlined"
                  onClick={() => setShowExportModal(true)}
                />
              </Box>
              {canUploadData && (
                <StyledContainedButton
                  startIcon={<IconImage src={upload_img} size="auto" />}
                  sx={{width:"165px"}}
                  size="large"
                  title="Upload Data"
                  variant="contained"
                  onClick={() => setShowUploadDataModal(true)}
                />
              )}
            </RightFilters>
          </Stack>
        </Grid>
        
        <Grid item xs={12} md={12} lg={6}>
        <HeaderSearchBox>
              <StyledTextInput
                type="text"
                placeholder="Search"
                sx={{width:"320px"}}
                handleChangeValue={filterTradeData}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <IconImage src={search_img} size="auto" />
                    </InputAdornment>
                  ),
                }}
              />
            </HeaderSearchBox>
        </Grid>
       
      </StyledHeadeButtons>
      {/* If the data exists from api also checks the data from api is 0 */}
      {(uploadProgress?.progress || uploadProgress?.progress === 0) &&
        uploadProgress.uploadTime !== null &&
        uploadProgress?.status === "inprocess" && (
          <ProcessBox
            progress={Math.round(uploadProgress.progress)}
            onClick={() => setShowLogs(true)}
            task={uploadProgress}
          />
        )}

      {/* Show error box when task failed */}
      {uploadProgress?.status === "error" && (
          <Box sx={{ maxWidth: 500, margin: "20px auto" }}>
            <PlaceholderCard
              title="Task Failed"
              description="An error occurred while processing your data. Please check your file and try again."
              imgUrl={crash_warning}
              isBordered
              centerContent
              cardStyles={{
                backgroundColor: '#fef2f2',
                borderColor: '#fecaca',
              }}
            />
            <Box sx={{ 
              display: 'flex', 
              gap: 2, 
              justifyContent: 'center',
              marginTop: 2
            }}>
              <Button 
                variant="outlined" 
                size="small"
                onClick={() => {
                  // Clear the upload progress to dismiss the error
                  // This could be enhanced to track dismissed errors
                }}
                sx={{ color: '#666' }}
              >
                Dismiss
              </Button>
              <Button 
                variant="contained" 
                size="small"
                onClick={() => setShowUploadDataModal(true)}
                sx={{ 
                  backgroundColor: '#d32f2f',
                  '&:hover': { backgroundColor: '#b71c1c' }
                }}
              >
                Try Again
              </Button>
            </Box>
          </Box>
        )}
     
      <DynamicTable
        data={filteredData as TradeDataTypes[]}
        columns={filteredColumns
          .filter((column) => !(column as any).isHidden)
          .map((col) => {
            const { isHidden, ...rest } = col as any;

            return rest as IColumn<TradeDataTypes>;
          })}
        isLoading={isLoading || isFetching} // show the loader if the client is not selected
        handleLimitChange={(e: number) => {
          setPage(INITIAL_PAGE);
          setLimit(e);
        }}
        sx={{
          flex: 1,
          "& tbody tr": {
            cursor: "auto ",
          },
          "& .MuiTable-root": {
            borderCollapse: "collapse",
          },
          "& .MuiTableRow-root:last-of-type .MuiTableCell-root": {
            borderBottom: "1px solid #e6e6e9 !important",
          },
          "& .MuiTableHead-root .MuiTableCell-root": {
            backgroundColor: "#E6ECED !important",
            color: "#030712 !important",
            fontWeight: "500 !important",
          },
          "& .MuiTableHead-root .MuiTableCell-root .MuiBox-root": {
            color: "#030712 !important",
            fontWeight: "500 !important",
          },
        }}
        handlePageChange={handlePageChange}
        stickyHeader
        hideScrollBars
        limit={limit}
        page={page}
        total={apiData?.getAllTradeData?.meta.countDocs ?? 0}
        hidePagination={false}
      />

      <Drawer anchor="right" open={showLogs} onClose={() => setShowLogs(false)}>
        <TimelineData />
      </Drawer>
      {showUploadDataModal && (
        <UploadData
          title="Upload Trade Data"
          subTitle="Upload your ACE files so we can compile and structure your trade data for analysis"
          show={showUploadDataModal}
          setShow={setShowUploadDataModal}
          buttonText="Confirm"
          buttonAction={(e) => createTradeDataFn(e)}
          btnLoader={isCreatingTradeData}
          setTimeStamp={setTimeStamp}
        />
      )}
      {showExportModal && (
        <TradeDataExportModal show={showExportModal} setShow={setShowExportModal} />
      )}
    </Stack>
    )}
    </>
  );
};

export default TradeData;
